'use client';

import type { TrackCustomParams } from './useAnalyticsEvent';

/**
 * Shared front interaction event contracts (SSD-4730) — web and mobile emit
 * the same canonical event names and action enums so EA fans out one signal
 * across Mixpanel/BigQuery. These replace the per-campaign / selector-bound
 * GTM GTM tags (banner clicks, nav clicks, review/filter interactions, etc.)
 * that would otherwise be lost when the GTM container is removed.
 *
 * `skipGTM: true` — the legacy tags were CLICK/URL-triggered with no dataLayer
 * consumer for these names, so pushing would only risk double counts.
 */

type TrackCustomFn = (params: TrackCustomParams) => void;

function emit(trackCustom: TrackCustomFn, eventName: string, params?: Record<string, unknown>): void {
  trackCustom({ eventName, params, skipGTM: true });
}

// --- banner_clicked: campaign/home banners (one event, banner_id property) ---
export interface BannerClickedParams {
  banner_id?: string | number;
  banner_name?: string;
  destination_url?: string;
  source_component?: string;
  ui_location?: string;
  position?: number;
  page_type?: string;
}
export const trackBannerClicked = (trackCustom: TrackCustomFn, params: BannerClickedParams): void =>
  emit(trackCustom, 'banner_clicked', { ...params });

// --- navigation_clicked: header / nav menu items ---
export type NavigationItem =
  | 'profile'
  | 'cart_header'
  | 'wishlist_header'
  | 'home'
  | 'categories'
  | 'trend'
  | 'shop_all'
  | 'back_to_top'
  | 'logo';
export interface NavigationClickedParams {
  destination_url?: string;
  source_component?: string;
  ui_location?: string;
  is_authenticated?: boolean;
  page_type?: string;
}
export const trackNavigationClicked = (
  trackCustom: TrackCustomFn,
  navigationItem: NavigationItem,
  params?: NavigationClickedParams,
): void => emit(trackCustom, 'navigation_clicked', { ...params, navigation_item: navigationItem });

// --- wishlist_clicked: header vs pdp ---
export type WishlistAction = 'header_wishlist_clicked' | 'pdp_wishlist_clicked';
export const trackWishlistClicked = (
  trackCustom: TrackCustomFn,
  action: WishlistAction,
  params?: Record<string, unknown>,
): void => emit(trackCustom, 'wishlist_clicked', { ...params, action });

// --- contact_link_clicked: footer email/phone ---
export type ContactType = 'email' | 'phone';
export const trackContactLinkClicked = (
  trackCustom: TrackCustomFn,
  contactType: ContactType,
  params?: Record<string, unknown>,
): void => emit(trackCustom, 'contact_link_clicked', { ...params, contact_type: contactType });

// --- review_interaction: PDP review section (AI review already instrumented) ---
export type ReviewAction =
  | 'write_review_clicked'
  | 'submit_review_clicked'
  | 'review_submitted'
  | 'review_images_view_all_clicked';
export const trackReviewInteraction = (
  trackCustom: TrackCustomFn,
  action: ReviewAction,
  params?: Record<string, unknown>,
): void => emit(trackCustom, 'review_interaction', { ...params, action });

// --- filter_applied: catalog/collection filters (AI compare filter is separate) ---
export type FilterType = 'skin_concern' | 'skin_type' | 'sort' | 'brand' | 'ingredient' | 'price';
export interface FilterAppliedParams {
  filter_value?: string;
  page_type?: string;
  source_component?: string;
  result_count?: number;
}
export const trackFilterApplied = (
  trackCustom: TrackCustomFn,
  filterType: FilterType,
  params?: FilterAppliedParams,
): void => emit(trackCustom, 'filter_applied', { ...params, filter_type: filterType });

// --- view_cart: cart drawer/page open ---
export interface ViewCartParams {
  cart_value?: number;
  currency?: string;
  item_count?: number;
  source_component?: string;
  ui_location?: string;
}
export const trackViewCart = (trackCustom: TrackCustomFn, params: ViewCartParams): void =>
  emit(trackCustom, 'view_cart', { ...params });

// --- search_results_viewed ---
export interface SearchResultsViewedParams {
  search_keyword?: string;
  result_count?: number;
  page_url?: string;
  source_component?: string;
}
export const trackSearchResultsViewed = (trackCustom: TrackCustomFn, params: SearchResultsViewedParams): void =>
  emit(trackCustom, 'search_results_viewed', { ...params });

// --- view_item_list: collection/search/brand/tag list views ---
export interface ViewItemListParams {
  list_id?: string;
  list_name?: string;
  list_type?: string;
  page_type?: string;
  visible_product_count?: number;
  source_component?: string;
}
export const trackViewItemList = (trackCustom: TrackCustomFn, params: ViewItemListParams): void =>
  emit(trackCustom, 'view_item_list', { ...params });

// --- progress_bar_interaction: free-shipping / BMSM progress bars ---
export type ProgressBarAction = 'viewed' | 'completed';
export interface ProgressBarParams {
  progress_name?: string;
  progress_percent?: number;
  source_component?: string;
  ui_location?: string;
}
export const trackProgressBarInteraction = (
  trackCustom: TrackCustomFn,
  action: ProgressBarAction,
  params?: ProgressBarParams,
): void => emit(trackCustom, 'progress_bar_interaction', { ...params, action });

// --- engagement_threshold_reached: dwell-time thresholds (60s mapping is SSD-4724) ---
export interface EngagementThresholdParams {
  page_type?: string;
  page_url?: string;
  source_component?: string;
}
export const trackEngagementThresholdReached = (
  trackCustom: TrackCustomFn,
  thresholdSeconds: number,
  params?: EngagementThresholdParams,
): void => emit(trackCustom, 'engagement_threshold_reached', { ...params, threshold_seconds: thresholdSeconds });
